import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate, Link } from "react-router-dom";
import {
    Loader2,
    Users,
    FileText,
    BellRing,
    Newspaper,
    Search as SearchIcon,
    X,
    Save,
    RotateCcw,
    KeyRound,
} from "lucide-react";
import { WorkspaceTabs } from "../components/workspace/Primitives";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "users", label: "Users" },
    { key: "bulletins", label: "Bulletins" },
    { key: "plans", label: "Plans & limits" },
    { key: "activity", label: "Activity log" },
    { key: "audit", label: "Audit log" },
];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("overview");

    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="container-edit py-14" data-testid="admin-forbidden">
                <h1 className="font-display text-3xl tracking-tight">403: Admins only</h1>
                <p className="mt-3 text-[#0D1B3E]/70">
                    This area is reserved for administrators. Return to the{" "}
                    <Link to="/app" className="underline">dashboard</Link>.
                </p>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="admin-dashboard">
            <div className="label-eyebrow">Administration</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                Admin
            </h1>
            <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                Manage users, subscriptions, content and platform limits. All endpoints
                are protected by the <code>admin</code> role server-side.
            </p>

            <div className="mt-8">
                <WorkspaceTabs tabs={TABS} active={tab} onChange={setTab} testIdPrefix="admin" />
            </div>

            <div className="mt-9">
                {tab === "overview" && <OverviewTab />}
                {tab === "users" && <UsersTab />}
                {tab === "bulletins" && <BulletinsTab />}
                {tab === "plans" && <PlansTab />}
                {tab === "activity" && <ActivityTab />}
                {tab === "audit" && <AuditLogTab />}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------
// Overview
// ----------------------------------------------------------------------------
function OverviewTab() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data));
    }, []);
    if (!stats) return <LoadingRow />;

    const tiles = [
        { icon: Users, label: "Users", value: stats.users.total, sub: `${stats.users.admin} admin · ${stats.users.suspended} suspended` },
        { icon: FileText, label: "Documents uploaded", value: stats.content.documents, sub: `${stats.content.conversations} conversations` },
        { icon: BellRing, label: "Watches", value: stats.content.watches, sub: `${stats.content.alerts} alerts filed` },
        { icon: Newspaper, label: "Bulletins", value: stats.content.bulletins, sub: `${stats.content.bulletins_draft} drafts` },
    ];

    return (
        <section data-testid="admin-overview">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-overview-tiles">
                {tiles.map((t, i) => (
                    <div key={i} className="card-flat p-6" data-testid={`admin-tile-${i}`}>
                        <div className="flex items-center gap-2 label-eyebrow">
                            <t.icon size={14} color="#C9A84C" />
                            {t.label}
                        </div>
                        <div className="mt-3 font-display text-4xl tracking-tight">
                            {t.value}
                        </div>
                        <div className="mt-2 text-xs text-[#0D1B3E]/55">{t.sub}</div>
                    </div>
                ))}
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-5">
                <div className="card-flat p-6">
                    <div className="label-eyebrow">Users by plan</div>
                    <ul className="mt-4 space-y-3">
                        {Object.entries(stats.users.by_plan).map(([k, n]) => (
                            <li key={k} className="flex items-center justify-between text-sm">
                                <span className="capitalize">{k}</span>
                                <span className="font-display text-2xl tracking-tight">
                                    {n}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="card-flat p-6">
                    <div className="label-eyebrow">Aggregate usage this period</div>
                    <div className="mt-3 font-display text-3xl tracking-tight">
                        {stats.usage.used} / {stats.usage.limit}{" "}
                        <span className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                            credits
                        </span>
                    </div>
                    <div className="mt-4 h-1.5 bg-[#0D1B3E]/10">
                        <div
                            className="h-full bg-[#C9A84C]"
                            style={{
                                width: `${
                                    stats.usage.limit
                                        ? Math.min(100, (stats.usage.used / stats.usage.limit) * 100)
                                        : 0
                                }%`,
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

// ----------------------------------------------------------------------------
// Users
// ----------------------------------------------------------------------------
function UsersTab() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [open, setOpen] = useState(null); // selected uid
    const [confirmDelete, setConfirmDelete] = useState(null); // uid pending confirm
    const [rowBusy, setRowBusy] = useState(null);

    const load = async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (planFilter) params.set("plan_key", planFilter);
        if (roleFilter) params.set("role", roleFilter);
        if (flaggedOnly) params.set("flagged", "true");
        params.set("limit", "100");
        const { data } = await api.get(`/admin/users?${params.toString()}`);
        setData(data);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planFilter, roleFilter, flaggedOnly]);

    const rowAction = async (uid, fn, okMsg) => {
        setRowBusy(uid);
        try {
            await fn();
            if (okMsg) toast.success(okMsg);
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Action failed");
        } finally {
            setRowBusy(null);
            setConfirmDelete(null);
        }
    };
    const markSafe = (uid) => rowAction(uid,
        () => api.post(`/admin/users/${uid}/mark-safe`), "Marked as safe");
    const suspendRow = (uid) => rowAction(uid,
        () => api.patch(`/admin/users/${uid}`, { suspended: true }), "Account suspended");
    const deleteRow = (uid) => rowAction(uid,
        () => api.post(`/admin/users/${uid}/delete-account`),
        "Deletion scheduled (30-day grace period); subscription cancelled");

    const exportCsv = async () => {
        try {
            const { data: blob } = await api.get("/admin/users/export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "eurolawhub-users.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            /* surfaced via network tab; keep silent */
        }
    };

    return (
        <section data-testid="admin-users">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    load();
                }}
                className="flex items-center gap-3 flex-wrap"
            >
                <div className="relative flex-1 min-w-[220px]">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                    <input
                        className="field pl-9"
                        placeholder="Search by email or name"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="admin-users-search"
                    />
                </div>
                <select
                    className="field max-w-[180px]"
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    data-testid="admin-users-plan-filter"
                >
                    <option value="">All plans</option>
                    <option value="free">Free</option>
                    <option value="professional">Professional</option>
                    <option value="team">Team</option>
                </select>
                <select
                    className="field max-w-[140px]"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    data-testid="admin-users-role-filter"
                >
                    <option value="">All roles</option>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                </select>
                <button
                    type="button"
                    onClick={() => setFlaggedOnly((v) => !v)}
                    className={`text-xs uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${
                        flaggedOnly
                            ? "bg-rose-700 text-white border-rose-700"
                            : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-rose-700 hover:text-rose-700"
                    }`}
                    data-testid="admin-users-flagged-filter"
                >
                    Suspicious only
                </button>
                <button
                    type="button"
                    onClick={exportCsv}
                    className="btn-secondary !py-2 text-xs"
                    data-testid="admin-users-export"
                >
                    Export CSV
                </button>
            </form>

            {!data ? (
                <LoadingRow />
            ) : (
                <div className="mt-6 card-flat overflow-x-auto" data-testid="admin-users-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-[#0D1B3E]/10">
                                <th className="px-4 py-3 label-eyebrow">User</th>
                                <th className="px-4 py-3 label-eyebrow">Role</th>
                                <th className="px-4 py-3 label-eyebrow">Plan</th>
                                <th className="px-4 py-3 label-eyebrow">Verified</th>
                                <th className="px-4 py-3 label-eyebrow">Trial</th>
                                <th className="px-4 py-3 label-eyebrow">Seats</th>
                                <th className="px-4 py-3 label-eyebrow">Status</th>
                                <th className="px-4 py-3 label-eyebrow">Signed up</th>
                                <th className="px-4 py-3 label-eyebrow">Last login</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((u) => (
                                <tr
                                    key={u.id}
                                    className="border-b border-[#0D1B3E]/5 hover:bg-[#0D1B3E]/[0.02]"
                                    data-testid={`admin-user-row-${u.id}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{u.name || u.email.split("@")[0]}</div>
                                        <div className="text-xs text-[#0D1B3E]/60">{u.email}</div>
                                        {u.suspicion_flagged && (
                                            <div
                                                className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5"
                                                title={(u.suspicion_reasons || []).join(" · ")}
                                                data-testid={`admin-user-suspicious-${u.id}`}
                                            >
                                                Suspicious
                                            </div>
                                        )}
                                        {u.suspicion_flagged && (u.suspicion_reasons || []).length > 0 && (
                                            <div className="mt-1 text-[11px] text-rose-700/90 max-w-[260px]">
                                                {u.suspicion_reasons.join(" · ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <RoleBadge role={u.role} />
                                    </td>
                                    <td className="px-4 py-3 capitalize">{u.plan_key}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {u.email_verified ? (
                                            <span className="text-emerald-700">Yes</span>
                                        ) : (
                                            <span className="text-amber-700">No</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#0D1B3E]/70">
                                        {u.on_trial
                                            ? `${u.trial_plan || "trial"} · ends ${u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "–"}`
                                            : "–"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#0D1B3E]/70">
                                        {u.team_seats ? `${u.team_seats}${u.team_role ? ` (${u.team_role})` : ""}` : "–"}
                                    </td>
                                    <td className="px-4 py-3">
                                        {u.deletion_requested_at ? (
                                            <span className="text-rose-700 text-xs uppercase tracking-[0.18em]">Deletion pending</span>
                                        ) : u.suspended ? (
                                            <span className="text-rose-700 text-xs uppercase tracking-[0.18em]">Suspended</span>
                                        ) : u.must_reset_password ? (
                                            <span className="text-amber-700 text-xs uppercase tracking-[0.18em]">Reset required</span>
                                        ) : (
                                            <span className="text-emerald-700 text-xs uppercase tracking-[0.18em]">Active</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#0D1B3E]/65">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "–"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#0D1B3E]/65">
                                        {u.last_login_at
                                            ? new Date(u.last_login_at).toLocaleString()
                                            : "–"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => setOpen(u.id)}
                                            className="text-xs uppercase tracking-[0.18em] text-[#C9A84C] hover:underline"
                                            data-testid={`admin-user-manage-${u.id}`}
                                        >
                                            Manage
                                        </button>
                                        {u.suspicion_flagged && (
                                            <div className="mt-2 flex items-center justify-end gap-2 text-[10px] uppercase tracking-[0.14em]">
                                                <button
                                                    type="button"
                                                    disabled={rowBusy === u.id}
                                                    onClick={() => markSafe(u.id)}
                                                    className="text-emerald-700 hover:underline disabled:opacity-50"
                                                    data-testid={`admin-user-mark-safe-${u.id}`}
                                                >
                                                    Mark safe
                                                </button>
                                                {!u.suspended && (
                                                    <button
                                                        type="button"
                                                        disabled={rowBusy === u.id}
                                                        onClick={() => suspendRow(u.id)}
                                                        className="text-amber-700 hover:underline disabled:opacity-50"
                                                        data-testid={`admin-user-quick-suspend-${u.id}`}
                                                    >
                                                        Suspend
                                                    </button>
                                                )}
                                                {!u.deletion_requested_at && (
                                                    <button
                                                        type="button"
                                                        disabled={rowBusy === u.id}
                                                        onClick={() =>
                                                            confirmDelete === u.id
                                                                ? deleteRow(u.id)
                                                                : setConfirmDelete(u.id)
                                                        }
                                                        className={`hover:underline disabled:opacity-50 ${
                                                            confirmDelete === u.id
                                                                ? "text-white bg-rose-700 px-2 py-0.5"
                                                                : "text-rose-700"
                                                        }`}
                                                        data-testid={`admin-user-quick-delete-${u.id}`}
                                                    >
                                                        {confirmDelete === u.id ? "Confirm delete?" : "Delete"}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {data.items.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-10 text-center text-[#0D1B3E]/60">
                                        No users match.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {data && (
                <div className="mt-3 text-xs text-[#0D1B3E]/55">
                    Showing {data.items.length} of {data.total}
                </div>
            )}

            {open && (
                <UserDetailModal
                    uid={open}
                    onClose={() => setOpen(null)}
                    onChanged={load}
                />
            )}
        </section>
    );
}

function RoleBadge({ role }) {
    return role === "admin" ? (
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-[#0D1B3E] text-[#F5F0E8]">
            admin
        </span>
    ) : (
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/65">
            user
        </span>
    );
}

function UserDetailModal({ uid, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [planKey, setPlanKey] = useState("");
    const [role, setRole] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        const { data } = await api.get(`/admin/users/${uid}`);
        setDetail(data);
        setPlanKey(data.user.plan_key);
        setRole(data.user.role);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    const save = async () => {
        setBusy(true);
        setError("");
        try {
            await api.patch(`/admin/users/${uid}`, { plan_key: planKey, role });
            await load();
            onChanged();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Save failed.");
        } finally {
            setBusy(false);
        }
    };
    const suspend = async (val) => {
        setBusy(true);
        try {
            await api.patch(`/admin/users/${uid}`, { suspended: val });
            await load();
            onChanged();
        } finally {
            setBusy(false);
        }
    };
    const resetUsage = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${uid}/reset-usage`);
            await load();
        } finally {
            setBusy(false);
        }
    };
    const forceResetPw = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${uid}/force-reset-password`);
            await load();
        } finally {
            setBusy(false);
        }
    };
    const generateResetLink = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/users/${uid}/reset-link`);
            await navigator.clipboard.writeText(data.reset_url).catch(() => {});
            toast.success(`Reset link copied to clipboard (valid ${data.ttl_min} min)`);
        } catch {
            toast.error("Could not generate reset link");
        } finally {
            setBusy(false);
        }
    };
    const [deleteArmed, setDeleteArmed] = useState(false);
    const markSafeModal = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${uid}/mark-safe`);
            toast.success("Marked as safe");
            await load();
            onChanged();
        } finally {
            setBusy(false);
        }
    };
    const deleteAccount = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${uid}/delete-account`);
            toast.success("Deletion scheduled (30-day grace period); subscription cancelled");
            await load();
            onChanged();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Delete failed.");
        } finally {
            setBusy(false);
            setDeleteArmed(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0D1B3E]/60 grid place-items-center p-4"
            onClick={onClose}
            data-testid="admin-user-modal-overlay"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-[#0D1B3E]/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7"
                data-testid="admin-user-modal"
            >
                {!detail ? (
                    <LoadingRow />
                ) : (
                    <>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="label-eyebrow">User</div>
                                <div className="mt-1 font-display text-2xl tracking-tight">
                                    {detail.user.name}
                                </div>
                                <div className="text-sm text-[#0D1B3E]/65">{detail.user.email}</div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                                data-testid="admin-user-modal-close"
                            >
                                <X size={22} />
                            </button>
                        </div>

                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-eyebrow">Plan</label>
                                <select
                                    className="field mt-2"
                                    value={planKey}
                                    onChange={(e) => setPlanKey(e.target.value)}
                                    data-testid="admin-user-plan-select"
                                >
                                    <option value="free">Free</option>
                                    <option value="professional">Professional</option>
                                    <option value="team">Team</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-eyebrow">Role</label>
                                <select
                                    className="field mt-2"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    data-testid="admin-user-role-select"
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 card-flat p-5">
                            <div className="label-eyebrow">Usage</div>
                            <div className="mt-2 font-display text-2xl tracking-tight">
                                {detail.usage?.queries_used ?? 0} / {detail.usage?.queries_limit ?? 0}
                            </div>
                            <div className="mt-2 text-xs text-[#0D1B3E]/60">
                                {detail.counts.documents} docs · {detail.counts.watches} watches · {detail.counts.conversations} conversations
                            </div>
                        </div>

                        {detail.user.suspicion_flagged && (
                            <div className="mt-4 border border-rose-200 bg-rose-50 p-4" data-testid="admin-user-modal-suspicion">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-rose-700">
                                    Flagged as suspicious
                                </div>
                                <ul className="mt-2 text-sm text-rose-800 list-disc pl-5 space-y-1">
                                    {(detail.user.suspicion_reasons || []).map((r) => (
                                        <li key={r}>{r}</li>
                                    ))}
                                </ul>
                                <div className="mt-3 flex items-center gap-3 flex-wrap">
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={markSafeModal}
                                        className="text-xs uppercase tracking-[0.18em] text-emerald-700 hover:underline"
                                        data-testid="admin-user-modal-mark-safe"
                                    >
                                        Mark as safe
                                    </button>
                                    {!detail.user.deletion_requested_at && (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() =>
                                                deleteArmed ? deleteAccount() : setDeleteArmed(true)
                                            }
                                            className={`text-xs uppercase tracking-[0.18em] hover:underline ${
                                                deleteArmed ? "bg-rose-700 text-white px-2.5 py-1" : "text-rose-700"
                                            }`}
                                            data-testid="admin-user-modal-delete"
                                        >
                                            {deleteArmed
                                                ? "Confirm: cancel subscription & schedule 30-day deletion"
                                                : "Delete account"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-4 text-sm text-rose-700" data-testid="admin-user-modal-error">
                                {error}
                            </div>
                        )}

                        <div className="mt-6 flex items-center gap-3 flex-wrap">
                            <button
                                type="button"
                                onClick={save}
                                className="btn-primary"
                                disabled={busy}
                                data-testid="admin-user-save"
                            >
                                {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={resetUsage}
                                className="btn-secondary"
                                disabled={busy}
                                data-testid="admin-user-reset-usage"
                            >
                                <RotateCcw size={14} /> Reset usage
                            </button>
                            <button
                                type="button"
                                onClick={forceResetPw}
                                className="btn-secondary"
                                disabled={busy}
                                data-testid="admin-user-force-reset-pw"
                            >
                                <KeyRound size={14} /> Force password reset
                            </button>
                            <button
                                type="button"
                                onClick={generateResetLink}
                                className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1.5"
                                disabled={busy}
                                data-testid="admin-user-reset-link"
                            >
                                <KeyRound size={14} /> Copy reset link
                            </button>
                            {detail.user.suspended ? (
                                <button
                                    type="button"
                                    onClick={() => suspend(false)}
                                    className="text-xs uppercase tracking-[0.18em] text-emerald-700 hover:underline ml-auto"
                                    disabled={busy}
                                    data-testid="admin-user-unsuspend"
                                >
                                    Unsuspend
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => suspend(true)}
                                    className="text-xs uppercase tracking-[0.18em] text-rose-700 hover:underline ml-auto"
                                    disabled={busy}
                                    data-testid="admin-user-suspend"
                                >
                                    Suspend
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------
// Bulletins
// ----------------------------------------------------------------------------
function BulletinsTab() {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("");

    const load = async () => {
        const params = new URLSearchParams();
        if (filter) params.set("status", filter);
        const { data } = await api.get(`/admin/bulletins?${params.toString()}`);
        setItems(data.items || []);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const togglePublish = async (b) => {
        const next = b.status === "published" ? "draft" : "published";
        await api.patch(`/admin/bulletins/${b.id}`, { status: next });
        load();
    };
    const remove = async (b) => {
        if (!window.confirm(`Delete bulletin "${b.title}"?`)) return;
        await api.delete(`/admin/bulletins/${b.id}`);
        load();
    };

    return (
        <section data-testid="admin-bulletins">
            <div className="flex items-center gap-2 flex-wrap">
                {[
                    { k: "", label: "All" },
                    { k: "published", label: "Published" },
                    { k: "draft", label: "Drafts" },
                ].map((f) => (
                    <button
                        key={f.k || "all"}
                        type="button"
                        onClick={() => setFilter(f.k)}
                        className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                            filter === f.k
                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                        }`}
                        data-testid={`admin-bulletins-filter-${f.k || "all"}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {!items ? (
                <LoadingRow />
            ) : (
                <ul className="mt-6 space-y-3" data-testid="admin-bulletins-list">
                    {items.map((b) => (
                        <li
                            key={b.id}
                            className="card-flat p-5 flex items-start gap-4 flex-wrap"
                            data-testid={`admin-bulletin-row-${b.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 label-eyebrow flex-wrap">
                                    {b.category && <span style={{ color: "#C9A84C" }}>{b.category}</span>}
                                    <span>·</span>
                                    <span>{b.status}</span>
                                    {b.generated_by_ai && (
                                        <>
                                            <span>·</span>
                                            <span>AI-generated</span>
                                        </>
                                    )}
                                </div>
                                <div className="mt-1 font-display text-lg tracking-tight">
                                    {b.title}
                                </div>
                                <div className="mt-1 text-xs text-[#0D1B3E]/60 truncate">
                                    /app/bulletins/{b.slug}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <Link
                                    to={`/app/bulletins/${b.slug}`}
                                    className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/65 hover:text-[#C9A84C]"
                                >
                                    View
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => togglePublish(b)}
                                    className="text-xs uppercase tracking-[0.18em] text-[#C9A84C] hover:underline"
                                    data-testid={`admin-bulletin-toggle-${b.id}`}
                                >
                                    {b.status === "published" ? "Unpublish" : "Publish"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(b)}
                                    className="text-xs uppercase tracking-[0.18em] text-rose-700 hover:underline"
                                    data-testid={`admin-bulletin-delete-${b.id}`}
                                >
                                    Delete
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

// ----------------------------------------------------------------------------
// Plans
// ----------------------------------------------------------------------------
function PlansTab() {
    const [data, setData] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});
    const [error, setError] = useState("");

    const load = async () => {
        const { data } = await api.get("/admin/plans");
        setData(data);
        const d = {};
        for (const p of data.items) {
            d[p.key] = {
                monthly_queries: p.monthly_queries,
                max_uploads: p.max_uploads,
                price_eur: p.price_eur,
            };
        }
        setDrafts(d);
    };
    useEffect(() => {
        load();
    }, []);

    const save = async (key) => {
        setSaving((s) => ({ ...s, [key]: true }));
        setError("");
        try {
            await api.patch(`/admin/plans/${key}`, drafts[key]);
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Save failed.");
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    };

    if (!data) return <LoadingRow />;
    return (
        <section data-testid="admin-plans">
            <p className="text-sm text-[#0D1B3E]/70 max-w-2xl">
                Override plan limits and prices. Changes apply immediately. Existing
                users on the plan have their usage counter ceiling updated.
            </p>
            <div className="mt-6 grid gap-5 lg:grid-cols-3">
                {data.items.map((p) => (
                    <div
                        key={p.key}
                        className="card-flat p-6"
                        data-testid={`admin-plan-${p.key}`}
                    >
                        <div className="label-eyebrow">Plan</div>
                        <div className="mt-1 font-display text-2xl tracking-tight">{p.name}</div>
                        <p className="mt-2 text-xs text-[#0D1B3E]/60">{p.description}</p>

                        <div className="mt-5 space-y-3">
                            <NumberField
                                label="Monthly queries"
                                value={drafts[p.key]?.monthly_queries ?? p.monthly_queries}
                                onChange={(v) =>
                                    setDrafts((d) => ({
                                        ...d,
                                        [p.key]: { ...d[p.key], monthly_queries: v },
                                    }))
                                }
                                testId={`admin-plan-${p.key}-queries`}
                            />
                            <NumberField
                                label="Max document uploads / month"
                                value={drafts[p.key]?.max_uploads ?? p.max_uploads}
                                onChange={(v) =>
                                    setDrafts((d) => ({
                                        ...d,
                                        [p.key]: { ...d[p.key], max_uploads: v },
                                    }))
                                }
                                testId={`admin-plan-${p.key}-uploads`}
                            />
                            <NumberField
                                label="Monthly price (EUR)"
                                value={drafts[p.key]?.price_eur ?? p.price_eur}
                                onChange={(v) =>
                                    setDrafts((d) => ({
                                        ...d,
                                        [p.key]: { ...d[p.key], price_eur: v },
                                    }))
                                }
                                testId={`admin-plan-${p.key}-price`}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => save(p.key)}
                            disabled={saving[p.key]}
                            className="btn-primary mt-5 w-full"
                            data-testid={`admin-plan-${p.key}-save`}
                        >
                            {saving[p.key] ? (
                                <Loader2 className="animate-spin" size={14} />
                            ) : (
                                <Save size={14} />
                            )}
                            Save overrides
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-9">
                <div className="label-eyebrow">Feature credit costs</div>
                <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {Object.entries(data.feature_costs).map(([k, v]) => (
                        <li
                            key={k}
                            className="card-flat p-3 text-sm flex items-center justify-between"
                        >
                            <code className="text-xs text-[#0D1B3E]/75">{k}</code>
                            <span className="font-display text-lg tracking-tight">
                                {v} <span className="text-xs text-[#0D1B3E]/55">cr</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {error && (
                <div className="mt-4 text-sm text-rose-700" data-testid="admin-plans-error">
                    {error}
                </div>
            )}
        </section>
    );
}

function NumberField({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="label-eyebrow">{label}</label>
            <input
                type="number"
                className="field mt-1"
                value={value ?? ""}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid={testId}
                min={0}
            />
        </div>
    );
}

// ----------------------------------------------------------------------------
// Activity
// ----------------------------------------------------------------------------
function ActivityTab() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/activity").then(({ data }) => setData(data));
    }, []);
    if (!data) return <LoadingRow />;
    return (
        <section data-testid="admin-activity" className="grid lg:grid-cols-2 gap-5">
            <ActivityList title="New users" items={data.new_users} render={(u) => (
                <>
                    <div className="text-sm font-medium">{u.name || u.email.split("@")[0]}</div>
                    <div className="text-xs text-[#0D1B3E]/65">{u.email} · {u.plan_key}</div>
                </>
            )} timeKey="created_at" testIdPrefix="admin-activity-newusers" />
            <ActivityList title="Recent logins" items={data.recent_logins} render={(u) => (
                <>
                    <div className="text-sm font-medium">{u.name || u.email.split("@")[0]}</div>
                    <div className="text-xs text-[#0D1B3E]/65">{u.email}</div>
                </>
            )} timeKey="last_login_at" testIdPrefix="admin-activity-logins" />
            <ActivityList title="Bulletins" items={data.bulletins} render={(b) => (
                <>
                    <div className="text-sm font-medium">{b.title}</div>
                    <div className="text-xs text-[#0D1B3E]/65">
                        {b.status}{b.generated_by_ai ? " · AI" : ""}
                    </div>
                </>
            )} timeKey="created_at" testIdPrefix="admin-activity-bulletins" />
            <ActivityList title="Watches" items={data.watches} render={(w) => (
                <>
                    <div className="text-sm font-medium">{w.name}</div>
                    <div className="text-xs text-[#0D1B3E]/65">scan: {w.last_scan_status}</div>
                </>
            )} timeKey="created_at" testIdPrefix="admin-activity-watches" />
        </section>
    );
}

function ActivityList({ title, items, render, timeKey, testIdPrefix }) {
    return (
        <div className="card-flat p-6" data-testid={testIdPrefix}>
            <div className="label-eyebrow">{title}</div>
            <ul className="mt-4 space-y-3">
                {items.length === 0 && (
                    <li className="text-sm text-[#0D1B3E]/55">No items.</li>
                )}
                {items.map((it, i) => (
                    <li
                        key={it.id || i}
                        className="flex items-start gap-3 border-b border-[#0D1B3E]/5 pb-3 last:border-b-0"
                    >
                        <div className="min-w-0 flex-1">{render(it)}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/45 shrink-0">
                            {it[timeKey] ? new Date(it[timeKey]).toLocaleString() : ""}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function LoadingRow() {
    return (
        <div className="mt-10 flex items-center gap-3 text-[#0D1B3E]/60">
            <Loader2 className="animate-spin" size={18} />
            <span className="label-eyebrow">Loading…</span>
        </div>
    );
}

function AuditLogTab() {
    const [items, setItems] = useState(null);
    const [action, setAction] = useState("");
    const [err, setErr] = useState("");

    async function load() {
        setItems(null);
        setErr("");
        try {
            const params = new URLSearchParams();
            if (action) params.set("action", action);
            params.set("limit", "200");
            const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not load audit log");
            setItems([]);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    return (
        <div className="space-y-6" data-testid="admin-audit-log">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Audit log</div>
                    <h2 className="font-display text-2xl mt-1 tracking-tight">
                        Sensitive actions across the platform
                    </h2>
                    <p className="mt-2 text-sm text-[#0D1B3E]/70 max-w-2xl">
                        Immutable record of admin changes, billing events, exports and
                        other high-signal actions. Filter by action to narrow the feed.
                    </p>
                </div>
                <select
                    className="field min-h-[44px] max-w-xs"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    data-testid="admin-audit-filter"
                >
                    <option value="">All actions</option>
                    <option value="admin.user.patch">Admin: user patched</option>
                    <option value="admin.user.reset-usage">Admin: usage reset</option>
                    <option value="admin.user.force-reset-password">Admin: forced password reset</option>
                    <option value="billing.checkout.completed">Billing checkout completed</option>
                    <option value="export.compliance.docx">Export: compliance DOCX</option>
                    <option value="export.argument.docx">Export: argument DOCX</option>
                    <option value="export.impact.docx">Export: impact DOCX</option>
                    <option value="export.bulletin.docx">Export: bulletin DOCX</option>
                    <option value="export.comparative.xlsx">Export: comparative XLSX</option>
                    <option value="export.alerts.xlsx">Export: alerts XLSX</option>
                    <option value="export.usage.xlsx">Export: usage XLSX</option>
                </select>
            </div>

            {err && (
                <p className="text-sm text-red-700" data-testid="admin-audit-error">
                    {err}
                </p>
            )}

            {!items ? (
                <LoadingRow />
            ) : items.length === 0 ? (
                <p className="text-sm text-[#0D1B3E]/60">No audit entries match.</p>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block border border-[#0D1B3E]/12 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0D1B3E] text-[#F5F0E8]">
                                <tr>
                                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.22em]">At</th>
                                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.22em]">User</th>
                                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.22em]">Action</th>
                                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.22em]">Target</th>
                                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.22em]">Outcome</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} className="border-t border-[#0D1B3E]/10">
                                        <td className="px-4 py-2 text-xs whitespace-nowrap">
                                            {it.at ? new Date(it.at).toLocaleString() : "–"}
                                        </td>
                                        <td className="px-4 py-2 text-xs">
                                            <div>{it.user_email || "–"}</div>
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                                {it.user_role || ""}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs">{it.action}</td>
                                        <td className="px-4 py-2 text-xs truncate max-w-[240px]">{it.target || "–"}</td>
                                        <td className="px-4 py-2 text-xs">{it.outcome || "success"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile card list */}
                    <ul className="md:hidden space-y-3" data-testid="admin-audit-mobile-list">
                        {items.map((it) => (
                            <li key={it.id} className="card-flat p-4">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono text-xs">{it.action}</span>
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                        {it.outcome || "success"}
                                    </span>
                                </div>
                                <div className="mt-2 text-xs text-[#0D1B3E]/80">
                                    {it.user_email || "–"} · {it.user_role || ""}
                                </div>
                                {it.target && (
                                    <div className="mt-1 text-xs truncate">{it.target}</div>
                                )}
                                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                    {it.at ? new Date(it.at).toLocaleString() : ""}
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

